import React from 'react';
import PropTypes from 'prop-types';
import { Modal, Button } from 'antd';
import PDFViewerCore from './PDFViewerCore';

/**
 * PDFViewerModal - Full-screen PDF viewer in a modal
 *
 * This component wraps PDFViewerCore in an Ant Design Modal for full-screen viewing.
 * Perfect for displaying example PDFs, reports, or any document that needs focused viewing.
 *
 * @param {boolean} isOpen - Whether the modal is visible
 * @param {string} pdfUrl - URL of the PDF file to display
 * @param {string} title - Modal title (default: "PDF Viewer")
 * @param {string} filename - Original filename for downloads (default: "document.pdf")
 * @param {function} onClose - Callback when modal is closed
 * @param {function} onDownload - Custom download handler (optional)
 * @param {string} width - Modal width (default: "95%")
 * @param {number} maxWidth - Maximum modal width in pixels (default: 1400)
 */
const PDFViewerModal = ({
  isOpen,
  pdfUrl,
  title = 'PDF Viewer',
  filename = 'document.pdf',
  onClose,
  onDownload,
  width = '95%',
  maxWidth = 1400
}) => {
  return (
    <Modal
      title={title}
      open={isOpen}
      onCancel={onClose}
      width={width}
      style={{ top: 10, maxWidth: `${maxWidth}px` }}
      bodyStyle={{ padding: 0, height: 'calc(95vh - 110px)' }}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>
      ]}
      destroyOnClose={true}
    >
      <PDFViewerCore
        pdfUrl={pdfUrl}
        filename={filename}
        showToolbar={true}
        toolbarPosition="top"
        onDownload={onDownload}
        containerStyle={{ height: '100%' }}
      />
    </Modal>
  );
};

PDFViewerModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  pdfUrl: PropTypes.string.isRequired,
  title: PropTypes.string,
  filename: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onDownload: PropTypes.func,
  width: PropTypes.string,
  maxWidth: PropTypes.number
};

export default PDFViewerModal;
